import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useThunkDispatch } from '@common/hooks';
import {
  Modal, ModalSubNavigation,
  ModalSubNavigationTitle,
  ModalSubNavigationItem,
} from '@common/components/modal';
import { Button } from '@common/components/button';
import { ScreenComponent } from '@modules/learning/components/screen-preview/component';
import { TemplatePreview } from './preview';
import * as formsSelector from '../../selectors';
import { fetchTemplateDetail } from '../../actions';

import type { StoreState } from '@common/types/store';
import type { FormTemplateStored, APIFormTemplateDetail } from '../../types';

require('./template-library.scss');

const mapStateToProps = (state: StoreState) => ({
  templates: formsSelector.getTemplates(state),
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & {
  show: boolean;
  onCreateForm: (templateId?: string) => void;
  onClose: () => void;
};

export const TemplateLibrary = connector(({
  templates,
  show,
  onCreateForm,
  onClose,
}: Props) => {
  const thunkDispatch = useThunkDispatch();
  const { t } = useTranslation();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [preview, setPreview] = useState<APIFormTemplateDetail | null>(null);

  useEffect(() => {
    if (previewId) thunkDispatch(fetchTemplateDetail(previewId));
  }, [previewId]);

  const handleOpenPreview = async (id: string) => {
    const { item } = await thunkDispatch(fetchTemplateDetail(id));

    setPreviewId(id);
    setPreview(item);
  };

  // Group templates by industry types
  const categories = templates.reduce<{ [key: string]: FormTemplateStored[] }>((acc, template) => {
    if (!template.industry_types) {
      acc.null.push(template);

      return acc;
    }

    template.industry_types.forEach((type) => {
      if (!acc[type]) acc[type] = [];

      acc[type].push(template);
    });

    return acc;
  }, {
    null: [],
  });

  return (
    <Modal
      fullWidthContent
      show={show}
      size="large"
      title={t('forms:template_library')}
      onExited={() => onClose()}
      content={(
        <div className="Templates__Container">
          <ModalSubNavigation>
            {Object.keys(categories).map((key: string) => (
              <>
                <ModalSubNavigationTitle>
                  {t('forms:form_template_category', { context: key.toLowerCase() })}
                </ModalSubNavigationTitle>
                {categories[key].map((template) => (
                  <ModalSubNavigationItem
                    image={template.icon.replace('.png', '-preview.png')}
                    onClick={() => handleOpenPreview(template.id)}
                    selected={template.id === previewId}
                  >
                    {template.title}
                  </ModalSubNavigationItem>
                ))}
              </>
            ))}
          </ModalSubNavigation>
          <div className="Templates__Content">
            {preview && (
              <TemplatePreview title={preview.title}>
                <div className="Screen">
                  <div className="Screen__Content">
                    {preview.screens.reduce<React.ReactNode[]>((acc, screen) => {
                      screen.components.forEach((component) => acc.push(<ScreenComponent item={component} />));

                      return acc;
                    }, [])}
                  </div>
                </div>
              </TemplatePreview>
            )}
          </div>
        </div>
      )}
      bodyClassName="!tw-p-0"
      footerClassName="!tw-justify-between"
      customFooter={(
        <>
          <Button onClick={() => onCreateForm()}>{t('forms:start_from_scratch')}</Button>
          <div className="tw-flex tw-gap-2">
            <Button onClick={() => onClose()}>{t('common:cancel')}</Button>
            {previewId ? (
              <Button type="primary" onClick={() => onCreateForm(previewId)}>{t('forms:select_template')}</Button>
          ) : <div />}
          </div>
        </>
      )}
    />
  );
});
